'use client';

import './payment-method-update-form.scss';

import { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.js';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.js';
import {
  Form,
  FormProps,
  useFormMethods,
} from '@sola/ark-core/molecules/form/form.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { CreditCardEnum } from '@sola/schema/dotcom/checkout/billingInfo.js';
import {
  GraphQLPaymentMethodBillingAddress,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';

import { SectionCardList } from '../../organisms/section-card-list/section-card-list';
import { UpdateACHFields } from './update-ach-fields';
import { UpdateCCFields } from './update-cc-fields';

/**
 * Get icon component
 * @TODO will change this after ark-icon refactor.
 */
const getIconName = (paymentMethod: GraphQLSupporterPaymentMethod) => {
  if (paymentMethod.creditCard?.cardType) {
    return (
      {
        [CreditCardEnum.Amex]: 'card-amex',
        'american express': 'card-amex',
        [CreditCardEnum.Discover]: 'card-discover',
        [CreditCardEnum.Mastercard]: 'caad-mastercard',
        'master card': 'card-mastercard',
        [CreditCardEnum.Visa]: 'card-visa',
      }[paymentMethod.creditCard.cardType.toLowerCase()] || 'credit-card-02' // what in tarnation?!
    );
  }

  if (paymentMethod.ach) {
    return 'bank';
  }

  // What else?
  return 'card';
};

/**
 * Get payment method details.
 */
const getDetails = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
) => {
  if (paymentMethod.creditCard) {
    return t('updatePaymentMethod.methodLabel.card', {
      cardType: paymentMethod.creditCard.cardType,
      lastFour: paymentMethod.creditCard.lastFour,
    });
  }
  if (paymentMethod.ach) {
    return t('updatePaymentMethod.methodLabel.ach', {
      lastFour: paymentMethod.ach.lastFourOfAccountNumber,
    });
  }
};

// Format billing address
const getAddress = (billingAddress?: GraphQLPaymentMethodBillingAddress) => {
  if (!billingAddress) {
    return '';
  }
  if (billingAddress.street && billingAddress.city) {
    return `${billingAddress.street}, ${billingAddress.city}, ${
      billingAddress.stateCode
    }, ${billingAddress.postalCode}`;
  }
};

const useComponentData = () => {
  // TODO
  // const { data } = useSupporterQuery(supporterAddressesQuery);
  // const address: GraphQLSupporterAddress = data;
  // return address;
};

export type PaymentMethodUpdateFormProps = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  children: React.ReactNode;
  onSubmit: FormProps['onSubmit'];
};

export const PaymentMethodUpdateForm: React.FC<
  PaymentMethodUpdateFormProps
> = ({ paymentMethod, children, onSubmit }) => {
  const { t } = useTranslation('my');
  // TODO;
  // const address = useComponentData();
  const formMethods = useFormMethods();

  const [selectedBillingAddress, setSelectedBillingAddress] = useState<
    string | undefined
  >(getAddress(paymentMethod.billingAddress || undefined));

  return (
    <div className="PaymentMethodUpdateForm">
      <Form
        onSubmit={onSubmit}
        formMethods={formMethods}
        isCustomError
        noValidate
      >
        <div>
          {/* ACH Details */}
          {paymentMethod.ach && (
            <SectionCardList
              heading={t('updatePaymentMethod.form.formTitle.bank')}
            >
              <div className="PaymentMethodUpdateForm__form w-full flex flex-col gap-comfy-xl">
                <div className="flex flex-row gap-comfy-xs md:gap-comfy-md items-center">
                  <ArkIcon
                    className="PaymentMethodUpdateSheet__icon PaymentMethodUpdateSheet__icon-bank rounded-sm p-snug-xl"
                    iconName={getIconName(paymentMethod) as IconNames}
                  />
                  <span className="block label-md label-moderate md:label-lg mb-snug-md">
                    {getDetails(paymentMethod, t)}
                  </span>
                </div>
                <Fieldset id="ach-fields">
                  <UpdateACHFields paymentMethod={paymentMethod} />
                </Fieldset>
              </div>
            </SectionCardList>
          )}

          {/* Card Info */}
          {paymentMethod.creditCard && (
            <>
              <SectionCardList
                heading={t('updatePaymentMethod.form.formTitle.card')}
              >
                <div className="PaymentMethodUpdateForm__form w-full flex flex-col gap-comfy-xl">
                  <div className="flex flex-row gap-comfy-xs md:gap-comfy-md items-center">
                    <ArkIcon
                      className="PaymentMethodUpdateSheet__icon"
                      iconName={getIconName(paymentMethod) as IconNames}
                    />
                    <span className="block label-md label-moderate md:label-lg mb-snug-md">
                      {getDetails(paymentMethod, t)}
                    </span>
                  </div>
                  <Fieldset id="cc-fields">
                    <UpdateCCFields paymentMethod={paymentMethod} />
                  </Fieldset>
                </div>
              </SectionCardList>

              {/* TODO */}
              {/* <Divider purpose="neutral" className="my-comfy-xl" />

              <div className="flex flex-col lg:flex-row w-full space-between gap-comfy-xl">
                <aside className="PaymentMethodUpdateForm__label">
                  <h3 className="label-potent md:label-potent label-md md:label-lg">
                    {t('updatePaymentMethod.form.formTitle.billingInfo')}
                  </h3>
                </aside>

                <div className="PaymentMethodUpdateForm__form w-full flex flex-col gap-comfy-lg">
                  <Select
                    name="billingAddress"
                    label={t('updatePaymentMethod.form.fields.billingInfo')}
                    placeholder={getAddress(
                      paymentMethod.billingAddress || undefined,
                    )}
                    defaultValue={selectedBillingAddress}
                    items={[
                      {
                        text: getAddress(
                          paymentMethod.billingAddress || undefined,
                        ),
                        value: 'paymentMethod.billingAddress', // why does the value have to be a string???
                      },
                    ]} // TODO: Populate supporter addresses
                    required
                  />

                  <LinkStyle iconBefore="plus" noPadding noUnderline size="lg">
                    <button type="button">
                      {t('updatePaymentMethod.form.address.addNew')}
                    </button>
                  </LinkStyle>
                </div>
              </div> */}
            </>
          )}

          <Divider purpose="neutral" className="my-comfy-xl" />

          {/* Recurring Giving Options */}
          <SectionCardList
            heading={t('updatePaymentMethod.form.formTitle.recurringGiving')}
          >
            <div className="PaymentMethodUpdateForm__form w-full flex flex-col gap-comfy-xl">
              <Checkbox
                label={t('updatePaymentMethod.form.fields.recurringGiving')}
                name="defualt"
              />
            </div>
          </SectionCardList>

          {/* Form submit */}
          {children}
        </div>
      </Form>
    </div>
  );
};
